import { useState, useEffect, useRef } from "react";
import { Mic, Square, Loader2, Volume2, VolumeX, Send, LifeBuoy, Eye, EyeOff, Plus, Check, ArrowLeft, RotateCcw, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAudioRecorder } from "@/hooks/useAudioRecorder";
import Waveform from "@/components/Waveform";
import { errorText } from "@/lib/errorText";

const LIVE_SEEN = "ff_live_intro_seen";
const newTurnId = () => `lt${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
window.__liveLatency = window.__liveLatency || [];

// Phase 7.2 — Live Fluent Friend: the learner speaks the TARGET language. Speak → Friend replies → speak again.
export function LiveFriend({ conversationId, targetCode, nativeCode, onExit, onConversationId }) {
  const { t } = useI18n();
  const [convId, setConvId] = useState(conversationId);
  const [messages, setMessages] = useState([]);
  const [stage, setStage] = useState(null);           // listening | understanding | thinking
  const [text, setText] = useState("");
  const [audioOn, setAudioOn] = useState(true);
  const [reveal, setReveal] = useState({});
  const [help, setHelp] = useState(null);             // null | {text, busy, result}
  const [recap, setRecap] = useState(null);
  const [failed, setFailed] = useState(null);         // {text, turn_id, mode}
  const [voiceErr, setVoiceErr] = useState({});       // reply text -> true when TTS failed
  const [intro, setIntro] = useState(!localStorage.getItem(LIVE_SEEN));
  const { recording, analyser, start, stop, audioMime, audioMeta } = useAudioRecorder();
  const audioRef = useRef(null);
  const endRef = useRef(null);
  const recFor = useRef(null);                        // "live" | "help"

  useEffect(() => {
    if (!conversationId) return;
    api.get(`/friend/conversations/${conversationId}`).then((r) => setMessages(r.data.messages || [])).catch(() => toast.error("Couldn't open conversation."));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (convId && convId !== conversationId) onConversationId?.(convId); }, [convId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, stage, help]);
  useEffect(() => () => stopAudio(), []);

  const stopAudio = () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } };
  // TTS only — never resends the turn. Failure keeps the text and shows a quiet note under the reply.
  const play = async (reply, timing) => {
    let audio;
    try {
      const r = await api.post("/friend/tts", { text: reply, live: true });
      if (typeof r?.data?.audio !== "string" || !r.data.audio) throw new Error("no audio");
      audio = r.data.audio;
    } catch (e) {
      if (e?.response?.status === 402) toast.error(t("friend.live.premium"));
      else setVoiceErr((v) => ({ ...v, [reply]: true }));
      return;
    }
    setVoiceErr((v) => { if (!v[reply]) return v; const n = { ...v }; delete n[reply]; return n; });
    if (recFor.current) return; // learner already started speaking — never compete with the mic
    stopAudio();
    const a = new Audio(`data:audio/mp3;base64,${audio}`);
    audioRef.current = a;
    a.onerror = () => setVoiceErr((v) => ({ ...v, [reply]: true }));
    if (timing) a.onplaying = () => { timing.audible = performance.now(); logLatency(timing); };
    try { await a.play(); } catch { /* autoplay blocked — Listen still works */ }
  };

  const sendTurn = async (body, mode, timing) => {
    const turn_id = body?.turn_id || newTurnId();
    setFailed(null); setStage("thinking");
    if (!body?.retry) setMessages((m) => [...m, { role: "learner", kind: "live", text: body.text, turn_id, mode }]);
    try {
      const r = await api.post("/friend/live/turn", { conversation_id: convId, text: body.text, turn_id, interaction_mode: mode });
      setConvId(r.data.conversation_id);
      setMessages((m) => [...m, { role: "friend", text: r.data.friend_reply, meaning: r.data.friend_reply_meaning }]);
      if (timing) { timing.text = performance.now(); timing.llm_ms = r.data.llm_ms; }
      setStage(null);
      if (audioOn) play(r.data.friend_reply, timing); else if (timing) logLatency(timing);
    } catch (e) {
      setStage(null);
      setFailed({ text: body.text, turn_id, mode });
      toast.error(errorText(e, t, "friend.live.replyFailed"));
    }
  };

  const toggleSpeak = async (target = "live") => {
    if (recording) {
      const timing = { end: performance.now() };
      const forWhat = recFor.current;
      const b64 = await stop(); recFor.current = null;
      if (!b64) { setStage(null); return; }
      setStage("understanding");
      try {
        const path = forWhat === "help" ? "/friend/transcribe" : "/friend/live/transcribe";
        const r = await api.post(path, { audio: b64, mime: audioMime.current, meta: audioMeta.current });
        timing.transcript = performance.now(); timing.stt_ms = r.data.stt_ms;
        setStage(null);
        if (forWhat === "help") setHelp((h) => ({ ...h, text: r.data.transcript }));
        else await sendTurn({ text: r.data.transcript }, "voice", timing);
      } catch (e) {
        setStage(null);
        toast.error(errorText(e, t, "friend.live.sttFailed"));
      }
      return;
    }
    stopAudio(); // barge-in: speaking always stops the Friend immediately
    try { recFor.current = target; await start(); setStage("listening"); }
    catch { recFor.current = null; setStage(null); toast.error(t("journal.micDenied")); }
  };

  const sendTyped = () => { const v = text.trim(); if (!v) return; setText(""); stopAudio(); sendTurn({ text: v }, "text"); };

  const askHelp = async () => {
    if (!help?.text?.trim()) return;
    setHelp((h) => ({ ...h, busy: true }));
    try {
      const r = await api.post("/friend/live/help", { conversation_id: convId, native_text: help.text.trim() });
      setConvId(r.data.conversation_id);
      setMessages((m) => [...m, { role: "help", native: help.text.trim(), target: r.data.target, meaning: r.data.meaning }]);
      setHelp(null);
    } catch (e) { setHelp((h) => ({ ...h, busy: false })); toast.error(errorText(e, t, "friend.live.replyFailed")); }
  };

  const end = async () => {
    stopAudio();
    if (!convId) { onExit(); return; }
    setStage("thinking");
    try { const r = await api.post(`/friend/conversations/${convId}/recap`); setRecap(r.data); }
    catch { setRecap({ short: true }); }
    finally { setStage(null); }
  };

  if (recap) return <LiveRecap recap={recap} targetCode={targetCode} onDone={onExit} />;

  return (
    <div className="nb-card p-4 sm:p-5" data-testid="friend-live">
      <div className="flex items-center justify-between gap-2 mb-3">
        <button onClick={() => { stopAudio(); onExit(); }} className="text-sm font-bold inline-flex items-center gap-1" data-testid="live-back"><ArrowLeft className="w-4 h-4" /> {t("friend.conversations")}</button>
        <div className="flex items-center gap-2">
          <button onClick={() => { if (audioOn) stopAudio(); setAudioOn(!audioOn); }} className="nb-btn-outline py-1.5 px-2 text-xs" title={t("friend.live.voice")} data-testid="live-audio-toggle">{audioOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}</button>
          <button onClick={end} className="nb-btn-outline py-1.5 px-3 text-xs" data-testid="live-end">{t("friend.live.end")}</button>
        </div>
      </div>
      {intro && (
        <div className="bg-sky border-2 border-ink rounded-xl p-3 mb-3 text-sm flex justify-between gap-3" data-testid="live-intro">
          <span>{t("friend.live.intro")}</span>
          <button onClick={() => { localStorage.setItem(LIVE_SEEN, "1"); setIntro(false); }} className="font-bold underline shrink-0" data-testid="live-intro-dismiss">OK</button>
        </div>
      )}
      {messages.length === 0 && (
        <div className="text-sm text-gray-600 mb-3" data-testid="live-starters">
          <div className="font-bold mb-1">{t("friend.live.ideas")}</div>
          <div className="flex flex-wrap gap-1.5">{["day", "plans", "happened", "work", "forward"].map((k) => <span key={k} className="nb-tag bg-paper">{t(`friend.live.starter.${k}`)}</span>)}</div>
        </div>
      )}
      <div className="space-y-2.5 max-h-[52vh] overflow-y-auto pe-1" data-testid="live-messages">
        {messages.map((m, i) => <LiveBubble key={i} m={m} i={i} lang={targetCode} reveal={reveal} setReveal={setReveal} onReplay={() => { stopAudio(); play(m.text); }} voiceFailed={!!voiceErr[m.text]} t={t} />)}
        {stage && <div className="text-sm text-gray-500 inline-flex items-center gap-2" data-testid={`live-stage-${stage}`}><Loader2 className="w-4 h-4 animate-spin" /> {t(`friend.live.${stage}`)}</div>}
        {failed && <button onClick={() => sendTurn({ text: failed.text, turn_id: failed.turn_id, retry: true }, failed.mode)} className="nb-btn-outline text-xs py-1.5 px-3" data-testid="live-retry"><RotateCcw className="w-3.5 h-3.5" /> {t("friend.live.retry")}</button>}
        <div ref={endRef} />
      </div>

      {help ? (
        <div className="mt-3 bg-butter border-2 border-ink rounded-xl p-3" data-testid="live-help-panel">
          <div className="flex justify-between items-center"><div className="text-sm font-bold">{t("friend.live.helpTitle")}</div><button onClick={() => setHelp(null)} data-testid="live-help-close"><X className="w-4 h-4" /></button></div>
          <div className="flex gap-2 mt-2">
            <textarea value={help.text || ""} onChange={(e) => setHelp({ ...help, text: e.target.value })} rows={2} dir="auto" lang={nativeCode} placeholder={t("friend.live.helpPlaceholder")} className="flex-1 p-2 border-2 border-ink rounded-lg bg-white text-sm" data-testid="live-help-input" />
            <button onClick={() => toggleSpeak("help")} className={`w-11 shrink-0 border-2 border-ink rounded-lg flex items-center justify-center ${recording ? "bg-coral text-white" : "bg-white"}`} data-testid="live-help-mic">{recording ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}</button>
          </div>
          <button onClick={askHelp} disabled={help.busy || !help.text?.trim()} className="nb-btn-primary w-full mt-2 py-2 text-sm disabled:opacity-60" data-testid="live-help-submit">{help.busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t("friend.live.helpGo")}</button>
        </div>
      ) : (
        <div className="mt-3">
          {recording && recFor.current === "live" && <div className="mb-2" data-testid="live-level-meter"><Waveform analyser={analyser} active color="#FF6B6B" height={36} bars={28} /></div>}
          <div className="flex gap-2">
            <button onClick={() => toggleSpeak("live")} disabled={stage === "understanding" || stage === "thinking"} className={`nb-btn-primary flex-1 py-3 disabled:opacity-60 ${recording ? "bg-coral" : ""}`} data-testid="live-speak">
              {recording ? <><Square className="w-5 h-5" /> {t("friend.live.stop")}</> : <><Mic className="w-5 h-5" /> {t("friend.live.speak")}</>}
            </button>
            <button onClick={() => { stopAudio(); setHelp({ text: "" }); }} className="nb-btn-outline px-3 text-sm" data-testid="live-help"><LifeBuoy className="w-4 h-4" /> <span className="hidden sm:inline">{t("friend.live.help")}</span></button>
          </div>
          <div className="flex gap-2 mt-2">
            <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendTyped()} dir="auto" lang={targetCode} placeholder={t("friend.live.type")} className="flex-1 p-2.5 border-2 border-ink rounded-lg bg-white text-sm" data-testid="live-type-input" />
            <button onClick={sendTyped} disabled={!text.trim() || stage === "thinking"} className="nb-btn-outline px-3 disabled:opacity-50" data-testid="live-type-send"><Send className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </div>
  );
}

function logLatency(tm) {
  const r = { stt_ms: tm.stt_ms, llm_ms: tm.llm_ms,
    speech_end_to_transcript_ms: tm.transcript ? Math.round(tm.transcript - tm.end) : null,
    transcript_to_text_ms: tm.transcript && tm.text ? Math.round(tm.text - tm.transcript) : null,
    text_to_audio_ms: tm.text && tm.audible ? Math.round(tm.audible - tm.text) : null,
    speech_end_to_text_ms: tm.text ? Math.round(tm.text - tm.end) : null,
    speech_end_to_audio_ms: tm.audible ? Math.round(tm.audible - tm.end) : null };
  window.__liveLatency.push(r);
  console.info("[live-latency]", JSON.stringify(r));
}

function LiveBubble({ m, i, lang, reveal, setReveal, onReplay, voiceFailed, t }) {
  if (m.role === "help") return (
    <div className="bg-butter/60 border-2 border-dashed border-ink rounded-xl p-3 text-sm" data-testid="live-help-bubble">
      <div className="text-xs text-gray-600" dir="auto">{t("friend.live.helped")}: {m.native}</div>
      <div className="font-bold mt-1" dir="auto" lang={lang}>{m.target}</div>
    </div>
  );
  const mine = m.role === "learner";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] border-2 border-ink rounded-2xl px-3.5 py-2.5 ${mine ? "bg-mint" : "bg-white"}`} data-testid={mine ? "live-learner-bubble" : "live-friend-bubble"}>
        <div dir="auto" lang={lang}>{mine ? m.text : m.text}</div>
        {!mine && (
          <div className="flex gap-3 mt-1.5 text-xs text-gray-500">
            <button onClick={onReplay} className="inline-flex items-center gap-1 hover:text-ink" data-testid="live-replay"><Volume2 className="w-3.5 h-3.5" /> {t("common.listen")}</button>
            {m.meaning && <button onClick={() => setReveal({ ...reveal, [i]: !reveal[i] })} className="inline-flex items-center gap-1 hover:text-ink" data-testid="live-show-meaning">{reveal[i] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />} {t("friend.live.meaning")}</button>}
          </div>
        )}
        {!mine && reveal[i] && <div className="text-xs text-gray-600 mt-1 italic" dir="auto" data-testid="live-meaning-text">{m.meaning}</div>}
        {!mine && voiceFailed && <div className="text-xs text-gray-500 mt-1" data-testid="live-voice-unavailable">{t("friend.live.voiceUnavailable")}</div>}
      </div>
    </div>
  );
}

function LiveRecap({ recap, targetCode, onDone }) {
  const { t } = useI18n();
  const [saved, setSaved] = useState({});
  const add = async (list) => {
    const fresh = list.filter((e) => !saved[e.expression]);
    if (!fresh.length) return;
    try {
      await api.post("/vocab/add", { items: fresh.map((e) => ({ word: e.expression, translation: e.translation || "", example_sentence: "" })), source: "fluent_friend" });
      setSaved((s) => { const n = { ...s }; fresh.forEach((e) => { n[e.expression] = true; }); return n; });
    } catch { toast.error("Couldn't save."); }
  };
  if (recap.short) return (
    <div className="nb-card p-8 text-center" data-testid="live-recap-short">
      <Check className="w-8 h-8 mx-auto" /><p className="font-cabinet font-bold text-xl mt-2">{t("friend.live.saved")}</p>
      <button onClick={onDone} className="nb-btn-primary mt-4 px-6 py-2" data-testid="live-recap-done">{t("lc.next")}</button>
    </div>
  );
  const exps = recap.expressions || [];
  return (
    <div className="nb-card p-5 space-y-4" data-testid="live-recap">
      <h3 className="font-cabinet font-extrabold text-2xl">{t("friend.live.recapTitle")}</h3>
      {exps.length > 0 && (
        <div>
          <div className="flex justify-between items-center"><h4 className="font-bold text-sm">{t("friend.live.keep")}</h4>
            <button onClick={() => add(exps)} className="text-xs font-bold underline" data-testid="live-add-all">{t("friend.live.addAll")}</button></div>
          {exps.map((e) => (
            <div key={e.expression} className="flex items-center justify-between gap-2 border-2 border-ink rounded-lg p-2.5 mt-2 bg-paper" data-testid="live-recap-expression">
              <div><div className="font-bold" dir="auto" lang={targetCode}>{e.expression}</div><div className="text-xs text-gray-600" dir="auto">{e.translation}</div></div>
              <button onClick={() => add([e])} disabled={saved[e.expression]} className="nb-btn-outline py-1 px-2 text-xs" data-testid="live-add-expression">{saved[e.expression] ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}</button>
            </div>
          ))}
        </div>
      )}
      {recap.more_natural?.natural && (
        <div data-testid="live-recap-natural"><h4 className="font-bold text-sm">{t("friend.live.moreNatural")}</h4>
          <div className="text-sm mt-1 text-gray-500 line-through" dir="auto" lang={targetCode}>{recap.more_natural.said}</div>
          <div className="font-bold" dir="auto" lang={targetCode}>{recap.more_natural.natural}</div>
          {recap.more_natural.why && <div className="text-xs text-gray-600" dir="auto">{recap.more_natural.why}</div>}</div>
      )}
      {recap.handled_well && <div data-testid="live-recap-well"><h4 className="font-bold text-sm">{t("friend.live.handledWell")}</h4><p className="text-sm" dir="auto">{recap.handled_well}</p></div>}
      {recap.practice_again && <div data-testid="live-recap-practice"><h4 className="font-bold text-sm">{t("friend.live.practiceAgain")}</h4><p className="text-sm font-bold" dir="auto" lang={targetCode}>{recap.practice_again}</p></div>}
      <button onClick={onDone} className="nb-btn-primary w-full py-3" data-testid="live-recap-done">{t("lc.next")}</button>
    </div>
  );
}
